<template>
  <div>
    <h3 class="display-4" @click="enableDebug($event)">
      Manage Form Template Permissions
    </h3>

    <o-list-toolbar
      :filters-list="filter_list"
      :search-fields-list="search_field_list"
      :selected-filter="selected_filter"
      :list-options="list_options"
      @reset_table="reset_table"
      @options_changed="update_table($event)"
      @filter_changed="update_filter($event)"
      :schema="schema"
      :query-select-options="options"
      :new-to="{ name: 'form_template_permission.create', params: { id: 0 } }"
      new-button-text="New Form Template Permission"
    >
      <template #middle>
        <o-list-multi-actions
          :schema="schema"
          :selection="selected"
          @delete="doDeleteItems"
          @merge="doMergeItems"
          enable-delete
          enable-merge
        />
      </template>
    </o-list-toolbar>

    <BTable
      striped
      bordered
      :fields="fields"
      :items="items"
      head-variant="light"
      class="o-list"
      thead-class="o-gradient-header"
      @row-selected="onRowSelected"
      :selectable="selected.selectable"
      :key="selected.selectable"
      :busy="working"
      no-local-sorting
      @sort-changed="change_sort"
      :sort-by="list_options.sort"
      :sort-desc="list_options.order === 'desc'"
    >
      <template v-slot:cell(_selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <span aria-hidden="true">&check;</span>
          <span class="sr-only">Selected</span>
        </template>
        <template v-else>
          <span aria-hidden="true">&nbsp;</span>
          <span class="sr-only">Not selected</span>
        </template>
      </template>
      <template #top-row="{ columns, fields }" v-if="filterRow">
        <filter-cell
          :key="field.key"
          v-for="field in fields"
          :field="field"
          :schema="schemaModel"
          :options="filter_row_options"
          :qbe="list_options.qbe"
          @filter="filter_changed"
        />
      </template>
      <template #cell(allow_create)="{ index, item }">
        <o-link-cell
          :to="{
            name: 'form_template_permission.edit',
            params: { id: item.id },
          }"
          :when="first_column_key === 'allow_create'"
        >
          <boolean-display :value="item.allow_create"></boolean-display>
        </o-link-cell>
      </template>
      <template #cell(allow_update)="{ index, item }">
        <o-link-cell
          :to="{
            name: 'form_template_permission.edit',
            params: { id: item.id },
          }"
          :when="first_column_key === 'allow_update'"
        >
          <boolean-display :value="item.allow_update"></boolean-display>
        </o-link-cell>
      </template>
      <template #cell(allow_view)="{ index, item }">
        <o-link-cell
          :to="{
            name: 'form_template_permission.edit',
            params: { id: item.id },
          }"
          :when="first_column_key === 'allow_view'"
        >
          <boolean-display :value="item.allow_view"></boolean-display>
        </o-link-cell>
      </template>
      <template #cell(form_template_version)="{ index, item }">
        <o-link-cell
          :to="{
            name: 'form_template_permission.edit',
            params: { id: item.id },
          }"
          :when="first_column_key === 'form_template_version'"
        >
          <lookup-display
            :list="optionFormTemplateVersionItemsById"
            :value="item.form_template_version"
            display-field="uuid"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>

      <template slot="empty">
        <p>
          <emoticon-sad-icon large></emoticon-sad-icon>
        </p>
        No Data Found. Please
        <router-link :to="{ name: 'form_template_permission.create' }"
          >create a new item</router-link
        >
      </template>
    </BTable>

    <o-list-paging
      :limit="list_options.limit"
      :page-options="rows_per_page_items"
      :offset="list_options.offset"
      :total-items="total_items"
      @change-limit="change_limit"
      @change-page="change_page"
    />
  </div>
</template>

<style></style>

<script>
import { BTable } from "bootstrap-vue";
import {
  CreateGroupPermissionMixin,
  CreateListPageMixin,
  MultiActionsMixin,
} from "@/vue-ocom/mixins";
import FetchAndUseCodeModMixin from "@/vue-ocom/mixins/FetchAndUseCodeModMixin";
import {
  fieldToTableColumn,
  GetSearchFields,
} from "@/vue-ocom/schema/schema_utils";
import { cleanUpModel } from "@/vue-ocom/schema/schema_vuetify.js";
import schema from "../../schema/index.js";

import OListToolbar from "@/vue-ocom/components/list/OListToolbar.vue";
import OListPaging from "@/vue-ocom/components/list/OListPaging.vue";
import DefaultDisplay from "@/vue-ocom/components/display/DefaultDisplay.vue";
import DateDisplay from "@/vue-ocom/components/display/DateDisplay.vue";
import NumberDisplay from "@/vue-ocom/components/display/NumberDisplay.vue";
import BooleanDisplay from "@/vue-ocom/components/display/BooleanDisplay.vue";
import LookupDisplay from "@/vue-ocom/components/display/LookupDisplay.vue";
import EmoticonSadIcon from "vue-material-design-icons/EmoticonSad.vue";
import EmailIcon from "vue-material-design-icons/Email.vue";
import LinkIcon from "vue-material-design-icons/Link.vue";
import FilterCell from "@/vue-ocom/components/list/filter/FilterCell.vue";

export default {
  components: {
    BTable,
    EmoticonSadIcon,
    EmailIcon,
    LinkIcon,
    OListPaging,
    OListToolbar,
    DateDisplay,
    DefaultDisplay,
    BooleanDisplay,
    NumberDisplay,
    LookupDisplay,
    FilterCell,
  },
  mixins: [
    CreateListPageMixin("FormTemplatePermission", "form_template_permission"),
    MultiActionsMixin,
    CreateGroupPermissionMixin("FormTemplatePermission"),
    FetchAndUseCodeModMixin(["FormTemplateVersion"]),
  ],
  data: function () {
    return {
      schemaModel: cleanUpModel(
        schema.FormTemplatePermission,
        {}
      ) /*Use this.schema instead */,

      search_field_list: GetSearchFields(schema.FormTemplatePermission),

      filterRow: true /* Set to False to disable filter row */,
      filter_list: [
        { name: "Active", code: "active", options: { offset: 0, sort: "id" } },
        {
          name: "Non Active",
          code: "inactive",
          options: { offset: 0, sort: "id" },
        },
        { name: "All", code: "all", options: { offset: 0, sort: "id" } },
      ],

      user_column_selection:
        null /* If user can customise the columns set them here */,
      column_override_params: {
        /* Put any column overrides here key by fieldname */
      },
    };
  },
  computed: {
    options() {
      return {
        FormTemplateVersion: {
          options: this.optionFormTemplateVersionItems,
          textField: "uuid",
          valueField: "id",
        },
      };
    },
    fields() {
      const vm = this;
      const selectedColumns = this.user_column_selection || [];
      const fields = selectedColumns.map(function (item) {
        return fieldToTableColumn(
          vm.schema,
          item,
          vm.column_override_params[item]
        );
      });

      return this.addSelectionColums(
        this.filterTableColumns(this.schema, fields)
      );
    },
    first_column_key() {
      if (this.fields) {
        return this.fields[0].key;
      } else {
        return null;
      }
    },
    filter_row_options() {
      // Options for the FilterRow components
      return {
        id: false /*No filter for this field */,
        allow_view: {},
        allow_create: {},
        allow_update: {},
        form_template_version: {
          options: this.optionFormTemplateVersionItems,
          loading: this.optionFormTemplateVersionWorking,
          "text-field": "uuid",
          "value-field": "id",
          "sort-field": "uuid",
          "blank-option-text": "",
        },
      };
    },
  },
};
</script>
